import * as React from "react";

import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import { Box, TextField, Button } from "@mui/material";
import { styled as muistyled } from "@mui/material/styles";
import * as yup from "yup";

import { Form } from "src/common/components/Form";
import { useUpdateDisk } from "src/domain/core/disks/api/updateDisk";
import { ALLOWED_MAX_DISK_SIZE } from "src/lib/const";

const DiagButton = muistyled(Button)(() => ({
  marginLeft: "15px",
  paddingRight: "25px",
  paddingLeft: "25px",
  float: "right",
}));

type EditDiskFormInput = {
  name: string;
  size: number;
  id: string;
};

interface EditDiskFormProps {
  disk: Disk;
  close: () => void;
}

const EditDiskForm: React.FC<EditDiskFormProps> = ({ disk, close }) => {
  const [originalDiskSize, setOriginalDiskSize] = React.useState(disk.size);
  const updateDisk = useUpdateDisk();

  const schema = yup.object().shape({
    size: yup
      .number()
      .positive()
      .integer()
      .min(disk.size + 1, `New size must be greater than current disk size of ${disk.size} gigabytes.`)
      .max(ALLOWED_MAX_DISK_SIZE, `Disk size cannot be greater than ${ALLOWED_MAX_DISK_SIZE}`)
      .required(),
    id: yup.string().required(),
  });

  React.useEffect(() => {
    setOriginalDiskSize(disk?.size);
  }, [disk?.size]);

  const handleEditSubmit = async (formInput: EditDiskFormInput, e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    await updateDisk(disk.id, formInput.size);
    close();
  };

  return (
    <Form<EditDiskFormInput, typeof schema>
      id="edit-disk"
      onSubmit={handleEditSubmit}
      autoComplete={"off"}
      options={{
        defaultValues: {
          id: disk?.id,
          size: disk?.size,
        },
      }}
      schema={schema}
      noValidate={true}
    >
      {({ register, formState }) => (
        <>
          <Box
            sx={{
              width: "100%",
              "& .MuiTextField-root": {
                marginBottom: "30px",
                marginTop: "10px",
                width: "100%",
              },
            }}
          >
            <TextField
              required
              id="disk-id"
              variant="outlined"
              label="ID"
              {...register("id", {
                required: "ID is required",
              })}
              helperText={formState.errors.id?.message || "The disk's ID. Cannot be altered."}
              error={formState.errors.id && true}
              disabled={true}
            />
            <TextField
              id="disk-size"
              label="Size (GB)"
              type="number"
              {...register("size", {
                required: "Size is required",
              })}
              error={formState.errors.size && true}
              helperText={formState.errors.size?.message || "The desired size of the disk, in gigabytes."}
              InputProps={{
                inputProps: {
                  min: originalDiskSize,
                  max: ALLOWED_MAX_DISK_SIZE,
                },
              }}
            />
            <DiagButton type="submit" variant="contained" startIcon={<CheckCircleOutlinedIcon />}>
              Update Disk
            </DiagButton>
            <DiagButton
              type="button"
              variant="outlined"
              startIcon={<CancelOutlinedIcon />}
              color="error"
              onClick={close}
            >
              Cancel
            </DiagButton>
          </Box>
        </>
      )}
    </Form>
  );
};

export default EditDiskForm;
